import { memo, useContext, useEffect, useState } from 'react';

import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { PlanningEntityType } from 'generated';
import { useRecoilState, useRecoilValue } from 'recoil';

import type { Event } from '../../types';
import type { EditAppointmentType } from 'RestAPIs/activities/types';
import { ZoomLevel as DetailZoomLevel } from '../../components/Header/ZoomLevelMenu.enums';

import { getAddressNotFound, getClientName, getHasWarnings } from '../../utils';

import {
  assignEmploymentEditAppointmentTypeAtom,
  dialogActiveAtom,
  dragActionActiveAtom,
  selectedContextMenuOptionAtom,
  shouldShowPopoverDialogAtom
} from 'components/roster-portal/activity-planning/atoms';

import { Box, Tooltip } from '@mui/material';
import {
  PopoverWarningDialog,
  EventTooltip,
  CustomContextMenu
} from 'components/roster-portal/activity-planning/components';

import { Chip } from './Chip';
import { CHIP_CLASS_NAME } from './Chip.constants';
import useContextMenuOptions from '../../components/ContextMenu/useContextMenuOptions';
import { PopOverConfirmationContext } from '../../PopOverConfirmationContext';
import {} from 'hooks/useChangedPropsInspection';

import { getZoomLevelProps } from './chip-zoom-level-props';
import { useSelector } from 'hooks';
import { SignalRow } from '../../components/SignalRow';
import dayjs from 'dayjs';

type Props = Event & {
  anchorEl?: HTMLElement;
  dialogActive?: boolean;
  handleRecurrenceSubmit?: (EditAppointmentType) => void;
  selected?: boolean;
  dragActionActive?: boolean;
  width?: number;
  startingPoint?: number;
  setHovered?: (hovered: boolean) => void;
  setDisabled?: (disabled: boolean) => void;
  onChangeMousePosition?: (pos: { x: number; y: number }) => void;
  onContextMenuClick?: (e: React.MouseEvent<HTMLElement>) => void;
  setAppointmentDropPositionX?: (object: Record<string, number> | null) => void;
  opacity?: number;
  isDragging?: boolean;
  hovered?: boolean;
};

export const AppointmentChip = memo((props: Props) => {
  const {
    id,
    activity,
    appointmentGrouping,
    clients,
    noteInternal,
    employees,
    isAssigned,
    isDragging,
    isEditable,
    isCreditable,
    isCredit,
    isOpen,
    isRecurring,
    isAbsent,
    hovered,
    location,
    warnings: {
      isOverQualified: overQualified,
      isUnderQualified: underQualified,
      startsTooEarly = false,
      startsTooLate = false,
      isInSync: isInSyncWithEcd = true,
      excludedEmployeeClientCombination = false,
      hasOverlap = false
    } = {},
    setHovered,
    setDisabled,
    timeWindow,
    width,
    selected,
    onChangeMousePosition,
    opacity,
    schedulingLabel,
    start
  } = props;
  const client = clients?.[0];

  const hasWarnings = getHasWarnings({
    startsTooEarly,
    startsTooLate,
    isInSyncWithEcd,
    excludedEmployeeClientCombination,
    isUrgent: !!timeWindow?.isUrgent,
    hasOverlap
  });

  const addressNotFound = getAddressNotFound(location, activity);

  const [anchorEl, setAnchorEl] = useState(null);
  const contextMenuOpen = Boolean(anchorEl);

  const dialogActive = useRecoilValue(dialogActiveAtom);

  const [
    {
      id: popoverOccurenceId,
      isUnassignment,
      scheduleStart: popoverScheduleStart,
      isAssignEmployment
    },
    setShouldShowPopoverDialog
  ] = useRecoilState(shouldShowPopoverDialogAtom);

  const [, setAssignEmploymentEditAppointmentType] = useRecoilState<
    EditAppointmentType | null | undefined
  >(assignEmploymentEditAppointmentTypeAtom);
  const [, setSelectedContextMenuOption] = useRecoilState(
    selectedContextMenuOptionAtom
  );

  const showPopoverDialog =
    popoverOccurenceId === id &&
    dayjs(popoverScheduleStart).isValid() &&
    popoverScheduleStart === dayjs(start).format('YYYY-MM-DD');

  const defaultDetailZoomLevel = useSelector(
    (state) => state.preferences.defaultZoomLevel
  );
  const detailZoomLevel = defaultDetailZoomLevel ?? DetailZoomLevel.STANDARD;
  const { showSignals, signalThreshold } = getZoomLevelProps(
    true,
    detailZoomLevel
  );

  const { onClose, onHandleAppointmentConfirmation } = useContext(
    PopOverConfirmationContext
  );

  const { options, handleOptionClick } = useContextMenuOptions({
    eventId: id,
    clientId: clients?.[0]?.id,
    schedulingLabel,
    isEditable,
    isAbsent,
    eventIsOpen: isOpen,
    isAssigned: !!isAssigned,
    isProject: false,
    isWorkPlace: false,
    hasAppointmentGrouping: !!appointmentGrouping,
    hasEmployee: !!employees?.[0],
    isCreditable,
    isCredit
  });

  const isClient = activity?.clientRelatedType !== 'NON_CLIENT_RELATED';
  const clientName = getClientName(client) ?? 'Onbekend';

  const allowToolTip =
    ((!isDragging && hovered) || isAbsent) &&
    !dialogActive &&
    !showPopoverDialog &&
    !anchorEl;

  function handleContextMenuClick(e) {
    e.preventDefault();
    if (options?.length > 0) {
      setAnchorEl(e.currentTarget);
    }
  }

  function handleContextMenuClose() {
    setAnchorEl(null);
    setHovered?.(false);
  }

  useEffect(() => {
    setDisabled?.(anchorEl || dialogActive || !!isDragging);
  }, [dialogActive, anchorEl]);

  return (
    <Tooltip
      arrow
      enterNextDelay={500}
      disableHoverListener={!allowToolTip}
      title={
        allowToolTip && (
          <EventTooltip
            event={props as Event}
            onMouseOver={() => setHovered?.(false)}
          />
        )
      }
    >
      <Box>
        <PopoverWarningDialog
          open={showPopoverDialog}
          onClose={() => {
            if (isAssignEmployment) {
              setShouldShowPopoverDialog({ id: '' });
              setSelectedContextMenuOption(null);
              return setAssignEmploymentEditAppointmentType(null);
            }

            onClose?.();
            setShouldShowPopoverDialog({ id: '' });
          }}
          onClickAppointment={(editAppointmentType: EditAppointmentType) => {
            if (isAssignEmployment) {
              setShouldShowPopoverDialog({ id: '' });
              return setAssignEmploymentEditAppointmentType(
                editAppointmentType
              );
            }

            onHandleAppointmentConfirmation?.(
              editAppointmentType,
              isUnassignment
            );
            setShouldShowPopoverDialog({ id: '' });
          }}
        >
          <Box
            id={id}
            sx={{
              opacity,
              userSelect: 'none'
            }}
            onMouseDown={(e) =>
              onChangeMousePosition?.({ x: e.clientX, y: e.clientY })
            }
          >
            <Chip
              width={width as number}
              isAbsent={isAbsent}
              isRecurring={isRecurring}
              isClient={isClient}
              selected={!!selected}
              hasWarnings={hasWarnings}
              handleRightClick={handleContextMenuClick}
              detailZoomLevel={detailZoomLevel}
            >
              <Box display="flex" flexDirection="column">
                <Box
                  sx={{
                    whiteSpace: 'nowrap',
                    fontSize: '12px',
                    height: '18px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textDecoration: isAbsent ? 'line-through' : 'none'
                  }}
                >
                  {isClient ? clientName : activity?.name}
                </Box>

                {showSignals && (
                  <SignalRow
                    schedulingLabel={schedulingLabel}
                    addressNotFound={addressNotFound}
                    noteInternal={noteInternal}
                    excludedEmployeeClientCombination={
                      excludedEmployeeClientCombination
                    }
                    hasOverlap={hasOverlap}
                    isAbsent={isAbsent}
                    isInSyncWithEcd={isInSyncWithEcd}
                    isRecurring={isRecurring}
                    locationType={location?.locationType}
                    overQualified={overQualified}
                    selected={selected}
                    signalThreshold={signalThreshold}
                    startsTooEarly={startsTooEarly}
                    startsTooLate={startsTooLate}
                    timeWindow={timeWindow}
                    underQualified={underQualified}
                    maxWidth={width}
                    sx={{
                      padding: 0
                    }}
                  />
                )}
              </Box>
            </Chip>
          </Box>
        </PopoverWarningDialog>
        <CustomContextMenu
          anchorEl={anchorEl}
          targetElClassName={CHIP_CLASS_NAME}
          open={contextMenuOpen}
          options={options}
          onOptionClick={handleOptionClick}
          onClose={handleContextMenuClose}
        />
      </Box>
    </Tooltip>
  );
});

interface DraggableChipProps extends ConditionalDraggableChipProps {
  disabled?: boolean;
}

export function Draggable(props: DraggableChipProps) {
  const {
    disabled,
    planningEntityId,
    selected,
    scheduleStart,
    hovered,
    isToPlan,
    setHovered,
    setDisabled,
    onContextMenuClick
  } = props;
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: props.id,
      disabled,
      data: {
        planningEntityId,
        scheduleStart,
        isToPlan
      }
    });

  const style = {
    transform: CSS.Translate.toString(transform),
    ...(isDragging && { opacity: 0 })
  };

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      <AppointmentChip
        {...props}
        setHovered={setHovered}
        setDisabled={setDisabled}
        selected={selected}
        isDragging={isDragging}
        hovered={hovered}
        onContextMenuClick={onContextMenuClick}
      />
    </div>
  );
}

interface ConditionalDraggableChipProps extends Props {
  planningEntityId?: string;
  planningEntityType?: PlanningEntityType;
  selected?: boolean;
  isOfMultipleSelection?: boolean;
  scheduleStart?: string;
  startingPoint?: number;
  appointmentDropPositionX: Record<string, number> | null;
  onSelectAppointment?: (id: string) => void;
  isToPlan: boolean;
}

export const ConditionalDraggableChip = memo(
  (props: ConditionalDraggableChipProps) => {
    const {
      appointmentDropPositionX,
      startingPoint,
      id,
      isAbsent,
      selected,
      isOfMultipleSelection,
      onSelectAppointment,
      isToPlan
    } = props;
    const [hovered, setHovered] = useState(false);
    const [disabled, setDisabled] = useState(false);

    const defaultDetailZoomLevel = useSelector(
      (state) => state.preferences.defaultZoomLevel
    );
    const detailZoomLevel = defaultDetailZoomLevel ?? DetailZoomLevel.STANDARD;

    const dragActionActive = useRecoilValue(dragActionActiveAtom);
    const hideOtherSelectedChipsOnDrag =
      isOfMultipleSelection && dragActionActive && selected;

    const style = {
      position: 'absolute' as const,
      left: appointmentDropPositionX?.[id] || startingPoint,
      top:
        detailZoomLevel === DetailZoomLevel.COMPACT
          ? '50%'
          : 'calc(50% - 10px)',
      zIndex: isAbsent ? 1 : 2,
      display: 'inline-block'
    };

    return (
      <Box
        style={style}
        onClick={() => onSelectAppointment?.(id)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => !dragActionActive && setHovered(false)}
      >
        {hovered ? (
          <Draggable
            {...props}
            isToPlan={isToPlan}
            disabled={disabled}
            selected={selected}
            hovered={hovered}
            setHovered={setHovered}
            setDisabled={setDisabled}
          />
        ) : (
          <AppointmentChip
            {...props}
            selected={selected}
            hovered={hovered}
            setHovered={setHovered}
            opacity={hideOtherSelectedChipsOnDrag ? 0 : 1}
            setDisabled={setDisabled}
          />
        )}
      </Box>
    );
  }
);
